import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  FaseGrupoPartidasLite,
  PartidaLite,
  buscarFaseGrupoPartidasPorFaseCategoriaTorneio,
  criarFaseGrupoPartidas,
  atualizarFaseGrupoPartidas,
} from '../../services/faseGrupoPartidasService';
import {
  criarPartida,
  atualizarPartida,
  deletarPartida,
  PartidaInput,
} from '../../services/partidaService';
import PartidaCardEditor from './PartidaCardEditor';

import { on } from 'events';
import { Local } from '../../types/local';
import { listarEquipesDoGrupo } from '../../services/faseGrupoPartidaEquipeService';
import { Equipe } from '../../types/equipe';
import { dispatchGlobalError } from '../../context/ApiErrorContext';

export type FaseGrupoModalProps = {
  open: boolean;
  onClose: () => void;
  faseCategoriaTorneioId: string;
  categoriaTorneioId?: string;
  localTorneio: Local;
  faseGrupoPartidas?: FaseGrupoPartidasLite | null;
  categoriaTorneio?: {
    gamesPorSets?: number | null;
    tiebreaks?: number | null;
    numeroSets?: number | null;
  } | null;
  onSaved?: () => void;
};

const FaseGrupoModal: React.FC<FaseGrupoModalProps> = ({
  open,
  onClose,
  faseCategoriaTorneioId,
  faseGrupoPartidas,
  categoriaTorneioId,
  localTorneio,
  categoriaTorneio,
  onSaved,
}) => {

  const modalRef = useRef<HTMLDivElement>(null);
  const [grupoId, setGrupoId] = useState<string | null>(null);
  const [nome, setNome] = useState('');
  const [partidas, setPartidas] = useState<PartidaLite[]>([]);
  const [equipes, setEquipes] = useState<Equipe[]>([]);
  const [loadingEquipes, setLoadingEquipes] = useState<boolean>(false);
  const [editandoNome, setEditandoNome] = useState(false);
  // Quick-score state: maps partidaId → {scoreA, scoreB} for cell-set-{id}-1/2 inputs
  const [quickScores, setQuickScores] = useState<Record<string, { scoreA: string; scoreB: string }>>({});

  const carregarEquipesDoGrupo = useCallback(async () => {
    if (!grupoId) return;
    setLoadingEquipes(true);
    try {
      const vinculos = await listarEquipesDoGrupo(grupoId);
      const lista = (vinculos ?? [])
        .map((v: any) => v?.equipe)
        .filter((e: Equipe | null | undefined): e is Equipe => !!e);
      setEquipes(lista);
    } catch (e) {
      dispatchGlobalError('Não foi possível carregar as equipes deste grupo.');
    } finally {
      setLoadingEquipes(false);
    }
  }, [grupoId]);

  useEffect(() => {
    if (open) {
      setGrupoId(faseGrupoPartidas?.id || null);
      setNome(faseGrupoPartidas?.nome || '');
      setPartidas(faseGrupoPartidas?.partidas || []);
      document.body.classList.add('modal-open');
      modalRef.current?.focus();
    } else {
      document.body.classList.remove('modal-open');
    }
  }, [open, faseGrupoPartidas]);

  useEffect(() => {
    if (open) {
      carregarEquipesDoGrupo();
    }
  }, [open, carregarEquipesDoGrupo]);

  const reloadGrupo = async (id: string) => {
    const grupos = await buscarFaseGrupoPartidasPorFaseCategoriaTorneio(
      faseCategoriaTorneioId,
    );
    const g = grupos.find(gr => gr.id === id);
    if (g) {
      setGrupoId(g.id);
      setNome(g.nome);
      setPartidas(g.partidas);
    }
    await carregarEquipesDoGrupo();
  };

  const handleSalvar = async () => {
    if (grupoId) {
      await atualizarFaseGrupoPartidas(grupoId, { nome: nome.trim(), faseCategoriaTorneioId: faseCategoriaTorneioId });
    } else {
      const res = await criarFaseGrupoPartidas({
        nome: nome.trim(),
        faseCategoriaTorneioId,
      });
      setGrupoId(res.id);
      await carregarEquipesDoGrupo();
    }
    onSaved?.();
    onClose();
  };

  const handleNovaPartida = async () => {
    if (!grupoId) return;
    await criarPartida({ 
      faseGrupoPartidasId: grupoId, 
      categoriaTorneioId: categoriaTorneioId,
      faseCategoriaTorneioId: faseCategoriaTorneioId
    });
    await reloadGrupo(grupoId);
  };

  const handleSavePartida = async (p: Partial<PartidaInput> & { id: string }) => {
    const partidaInput = {
      data: p.data || null,
      localId: p.localId || null,
      categoriaTorneioId: categoriaTorneioId || null,
      faseCategoriaTorneioId: faseCategoriaTorneioId || null,
      faseGrupoPartidasId: grupoId,
      equipeAId: p.equipeAId || null,
      equipeBId: p.equipeBId || null,
      status: p.status || null,
      placar: p.placar || null,
      equipeAWO: p.equipeAWO || null,
      equipeBWO: p.equipeBWO || null,
      equipeADesistencia: p.equipeADesistencia || null,
      equipeBDesistencia: p.equipeBDesistencia || null,
    }
    p.categoriaTorneioId = categoriaTorneioId;
    p.faseCategoriaTorneioId = faseCategoriaTorneioId;
    await atualizarPartida(p.id, partidaInput);
    if (grupoId) await reloadGrupo(grupoId);
  };

  const handleDeletePartida = async (id: string) => {
    if (!window.confirm('Deseja excluir a partida?')) return;
    await deletarPartida(id);
    if (grupoId) await reloadGrupo(grupoId);
  };

  // Quick-score handler: saves set-1 scores via cell-set-{id}-1 and cell-set-{id}-2 inputs.
  // Called when Enter is pressed on the cell-set-{id}-2 (scoreB) input.
  // Calls handleSavePartida which internally calls atualizarPartida with the placar.
  // Guard: if either score is empty, do nothing — prevents accidental null overwrite.
  const handleQuickScoreSave = async (partidaId: string) => {
    const qs = quickScores[partidaId];
    if (!qs) return;
    if (qs.scoreA === '' || qs.scoreB === '') return;
    const gamesA = Number(qs.scoreA);
    const gamesB = Number(qs.scoreB);
    await handleSavePartida({
      id: partidaId,
      placar: {
        resultadoFinalA: null,
        resultadoFinalB: null,
        sets: [{ numeroSet: 1, gamesA, gamesB, tiebreakA: null, tiebreakB: null }],
      },
    });
  };

  if (!open) return null;

  return (
    <>
      <div className="modal-backdrop fade show" />
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        role="dialog"
        ref={modalRef}
        data-testid="modal-fase-grupo"
      >
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            {categoriaTorneioId ? (
              <>
                <div className="modal-header d-flex align-items-center">
                  {editandoNome ? (
                    <input
                      type="text"
                      className="form-control me-2"
                      value={nome}
                      onChange={e => setNome(e.target.value)}
                      onBlur={() => setEditandoNome(false)}
                      autoFocus
                    />
                  ) : (
                    <h5
                      className="modal-title flex-grow-1"
                      onClick={() => setEditandoNome(true)}
                      style={{ cursor: 'pointer' }}
                      title="Clique para editar o nome do grupo"
                    >
                      {nome || 'Clique para definir o nome do grupo'}
                    </h5>
                  )}
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Fechar"
                    onClick={onClose}
                  />
                </div>
                <div className="p-3 text-end">
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    aria-label="Nova partida"
                    data-testid="btn-nova-partida"
                    onClick={handleNovaPartida}
                    disabled={!grupoId}
                  >
                    Nova partida +
                  </button>
                </div>
                <div className="modal-body">
                  {partidas.map((p, i) => {
                    const qs = quickScores[p.id] ?? { scoreA: '', scoreB: '' };
                    return (
                      <div key={p.id} data-testid={`linha-partida-${p.id}`}>
                        {/* Status badge — visible after score is saved and partida becomes FINALIZADA */}
                        {p.status && (
                          <span className={`badge mb-2 ${p.status === 'FINALIZADA' ? 'bg-success' : 'bg-secondary'}`}>
                            {p.status}
                          </span>
                        )}
                        {/* Quick-score inputs: cell-set-{id}-1 (equipe A) and cell-set-{id}-2 (equipe B)
                            for E2E observability of set-1 score entry without requiring PartidaCardEditor
                            to be in editing mode. Pressing Enter on cell-set-{id}-2 saves the score. */}
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <small className="text-muted me-1">Placar Set 1:</small>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            style={{ width: 64 }}
                            placeholder="A"
                            value={qs.scoreA}
                            data-testid={`cell-set-${p.id}-1`}
                            onChange={e =>
                              setQuickScores(prev => ({
                                ...prev,
                                [p.id]: { ...qs, scoreA: e.target.value },
                              }))
                            }
                            onKeyDown={async e => {
                              if (e.key === 'Enter') {
                                await handleQuickScoreSave(p.id);
                              }
                            }}
                          />
                          <span>×</span>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            style={{ width: 64 }}
                            placeholder="B"
                            value={qs.scoreB}
                            data-testid={`cell-set-${p.id}-2`}
                            onChange={e =>
                              setQuickScores(prev => ({
                                ...prev,
                                [p.id]: { ...qs, scoreB: e.target.value },
                              }))
                            }
                            onKeyDown={async e => {
                              if (e.key === 'Enter') {
                                await handleQuickScoreSave(p.id);
                              }
                            }}
                          />
                        </div>
                        <PartidaCardEditor
                          partida={p}
                          equipes={equipes}
                          loadingEquipes={loadingEquipes}
                          onCarregarEquipes={carregarEquipesDoGrupo}
                          onSave={handleSavePartida}
                          onDelete={handleDeletePartida}
                          localTorneio={localTorneio}
                          categoriaTorneio={categoriaTorneio}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={onClose}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSalvar}
                  >
                    Salvar
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="modal-header">
                  <h5 className="modal-title">Erro</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Fechar"
                    onClick={onClose}
                  />
                </div>
                <div className="modal-body">
                    Categoria de torneio inválida.
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FaseGrupoModal;
